import axios from 'axios';
import { AnchorProvider } from '@project-serum/anchor';
import { AnchorWallet } from '@solana/wallet-adapter-react';
import { ConfirmOptions, Connection, PublicKey } from '@solana/web3.js';
import * as anchor from '@project-serum/anchor';
import { formatUnits } from 'ethers/lib/utils';
import ConsoleHelper from './consoleHelper';
import { URL_SERVER_INFO } from './solchickConsts';
import { toPublicKey } from './solanaHelper';
import { NETWORK_IS_TEST } from './consts';

const CLAIM_START_TIME = NETWORK_IS_TEST
  ? '2022-10-13 00:00:00+00'
  : '2022-11-13 00:00:00+00';

export enum ClaimRequestStatusCode {
  NONE = 0,
  START,
  SUCCESS,
  SUBMITTING,
  FAILED = 101,
}

export enum ClaimRequestErrorCode {
  NO_ERROR,
  SERVER_INVALID,
  SUBMIT_FAILED,
}

export interface IClaimInfo {
  totalAllocationAmount: number;
  totalClaimedAmount: number;
  claimableAmount: number;
  unvestedAmount: number;
  nextUnlockDate: Date | undefined | null;
  nextUnlockAmount: number;
  totalPeriod: number;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  merkleInfo: any;
}

export const getServerInfo = async () => {
  try {
    const result = await axios.get(URL_SERVER_INFO());
    if (result && result.data && result.data.success) {
      return true;
    }
  } catch (e) {
    ConsoleHelper(`getServerInfo: ${e}`);
  }
  return false;
};

export async function getAnchorProvider(
  solanaConnection: Connection,
  walletSolana: unknown,
) {
  const opts = {
    preflightCommitment: 'confirmed',
  };
  if (!solanaConnection || !walletSolana) {
    return null;
  }
  return new AnchorProvider(
    solanaConnection,
    walletSolana as unknown as AnchorWallet,
    opts.preflightCommitment as unknown as ConfirmOptions,
  );
}

export const claimVestingSchedules =
  process.env.NODE_ENV === 'production'
    ? [
        '2021-12-13T12:00:00.000Z',
        '2021-12-13T14:00:00.000Z',
        '2022-01-13T14:00:00.000Z',
        '2022-01-23T14:00:00.000Z',
        '2022-01-24T14:00:00.000Z',
        '2022-02-12T17:02:33.000Z',
        '2022-02-12T17:03:59.000Z',
        '2022-02-12T17:36:18.000Z',
        '2022-02-12T17:36:48.000Z',
        '2022-02-12T17:37:22.000Z',
        '2022-02-12T17:37:45.000Z',
        '2022-02-13T14:00:00.000Z',
        '2022-02-13T13:49:16.000Z',
        '2022-02-13T14:00:00.000Z',
        '2022-03-13T14:00:00.000Z',
        '2022-04-13T14:00:00.000Z',
        '2022-05-13T14:00:00.000Z',
        '2022-06-13T14:00:00.000Z',
        '2022-07-13T14:00:00.000Z',
        '2022-08-13T14:00:00.000Z',
        '2022-09-13T14:00:00.000Z',
        '2022-10-13T14:00:00.000Z',
        '2022-11-13T14:00:00.000Z',
        '2022-12-13T14:00:00.000Z',
        // '2023-01-13T14:00:00.000Z',
        // '2023-02-13T14:00:00.000Z',
        // '2023-03-13T14:00:00.000Z',
        // '2023-04-13T14:00:00.000Z',
        // '2023-05-13T14:00:00.000Z',
        // '2023-06-13T14:00:00.000Z',
        // '2023-07-13T14:00:00.000Z',
        // '2023-08-13T14:00:00.000Z',
        // '2023-09-13T14:00:00.000Z',
        // '2023-10-13T14:00:00.000Z',
        // '2023-11-13T14:00:00.000Z',
        // '2023-12-13T14:00:00.000Z',
        // '2024-01-13T14:00:00.000Z',
        // '2024-02-13T14:00:00.000Z',
        // '2024-03-13T14:00:00.000Z',
      ]
    : [
        '2021-12-13T12:00:00.000Z',
        '2021-12-13T14:00:00.000Z',
        '2022-01-13T14:00:00.000Z',
        '2022-01-23T14:00:00.000Z',
        '2022-01-24T14:00:00.000Z',
        '2022-02-12T17:02:33.000Z',
        '2022-02-12T17:03:59.000Z',
        '2022-02-12T17:36:18.000Z',
        '2022-02-12T17:36:48.000Z',
        '2022-02-12T17:37:22.000Z',
        '2022-02-12T17:37:45.000Z',
        '2022-02-13T14:00:00.000Z',
        '2022-02-13T13:49:16.000Z',
        '2022-02-13T14:00:00.000Z',
        '2022-03-13T14:00:00.000Z',
      ];

export const isWithin5Days = () => {
  const today = new Date();
  const futureDatesWithToday = claimVestingSchedules
    .filter((date) => new Date(date).getTime() - today.getTime() > 0)
    .map((date) => ({
      date,
      diff: Math.abs(new Date(date).getTime() - today.getTime()),
    }));
  const sortedFutureDatesWithToday = futureDatesWithToday.sort(
    (a, b) => a.diff - b.diff,
  );
  let nearestFutureDate =
    claimVestingSchedules[claimVestingSchedules.length - 1];
  if (sortedFutureDatesWithToday && sortedFutureDatesWithToday[0]) {
    nearestFutureDate = sortedFutureDatesWithToday[0].date;
  }
  const diff = Math.abs(
    new Date(nearestFutureDate).getTime() - today.getTime(),
  );
  return {
    nearestFutureDate,
    isWithin5Days: diff < 5 * 24 * 60 * 60 * 1000,
  };
};

export const DISTRIBUTOR_HANDLE = 'chicks';
export const USER_CLAIM_STATUS_PDA_SEED = 'user_status';

export async function getDistributorKey(programId: string | PublicKey) {
  const [distributorAccountKey] = await PublicKey.findProgramAddress(
    [Buffer.from(DISTRIBUTOR_HANDLE)],
    toPublicKey(programId),
  );
  return distributorAccountKey;
}
export async function getUserClaimStatusKey(
  programId: string | PublicKey,
  userWalletKey: string | PublicKey,
) {
  return PublicKey.findProgramAddress(
    [
      toPublicKey(userWalletKey).toBuffer(),
      Buffer.from(DISTRIBUTOR_HANDLE),
      Buffer.from(USER_CLAIM_STATUS_PDA_SEED),
    ],
    toPublicKey(programId),
  );
}

export const getDays = (date: number | string | Date) =>
  Math.floor(new Date(date).getTime() / 1000 / 86400);

export function getNextDay() {
  return new Date((getDays(new Date()) + 1) * 86_400_000);
}

export function parseClaimInfo(
  userInfo: any,
  claimedAmount: anchor.BN,
): IClaimInfo {
  ConsoleHelper(`parseClaimInfo`, userInfo, claimedAmount.toString());
  const startDay = Math.floor(userInfo.start_time / 86400);
  const nowDay = getDays(new Date());
  const passedDay = nowDay >= startDay ? nowDay - startDay + 1 : 0;
  const totalDays = Math.floor(userInfo.period / 86400);
  ConsoleHelper(`startDay`, startDay, `passedDay`, passedDay, `totalDays`, totalDays);
  let unlockedAmount = 0;
  const totalAmount = Number(formatUnits(userInfo.amount.toString(), 9));
  const prevUnlockedAmount = Number(formatUnits(userInfo.unlocked_amount.toString(), 9));
  let dailyClaimAmount = 0;
  if (totalDays > passedDay) {
    dailyClaimAmount = totalAmount / totalDays;
    unlockedAmount = dailyClaimAmount * passedDay;
  } else if (totalDays <= passedDay) {
    unlockedAmount = totalAmount;
    dailyClaimAmount = 0;
  }
  const totalClaimedAmountOnChain = Number(
    formatUnits(claimedAmount.toString(), 9),
  );
  let claimableAmount = unlockedAmount + prevUnlockedAmount;
  claimableAmount =
    claimableAmount > totalClaimedAmountOnChain
      ? claimableAmount - totalClaimedAmountOnChain
      : 0;
  const unvestedAmount = totalAmount - unlockedAmount;
  const nextUnlockDate = dailyClaimAmount > 0 ? getNextDay() : null;
  const prevClaimedAmount = Number(
    userInfo.total_claimed_amount ? userInfo.total_claimed_amount : 0,
  );
  const totalAllocationAmount =
    totalAmount + prevUnlockedAmount + prevClaimedAmount;
  const totalClaimedAmount = totalClaimedAmountOnChain + prevClaimedAmount;

  return {
    totalAllocationAmount,
    totalClaimedAmount,
    claimableAmount,
    unvestedAmount,
    nextUnlockDate,
    nextUnlockAmount: dailyClaimAmount,
    totalPeriod: totalDays,
    merkleInfo: userInfo,
  };
}

export function getRemainingTime() {
  const nextUnlockTs =
    (Math.floor(new Date().getTime() / 1000 / 86400) + 1) * 86400;
  const nowTs = Math.floor(new Date().getTime() / 1000);
  return nextUnlockTs - nowTs;
}
